import React from 'react';
import { Container, Typography, Box, Paper, Accordion, AccordionSummary, AccordionDetails, Divider } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO/SEO';
import StarrySky from '../components/StarrySky';

interface FAQItem {
  question: string;
  answer: string;
}

const FAQ: React.FC = () => {
  const { t, i18n } = useTranslation();

  // Récupérer les questions/réponses depuis les traductions
  const faqItems: FAQItem[] = Array.from({ length: 10 }, (_, i) => ({
    question: t(`faq.questions.q${i + 1}`),
    answer: t(`faq.questions.a${i + 1}`)
  })).filter(item => item.question !== `faq.questions.q${item.question.slice(-1)}` && item.answer !== `faq.questions.a${item.answer.slice(-1)}`);

  return (
    <>
      <SEO 
        title={t('faq.title')} 
        description={t('faq.description')}
        keywords={t('faq.keywords')}
      />
      <StarrySky />
      <Container maxWidth="lg" sx={{ pt: 10, pb: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" component="h1" sx={{ 
            color: 'white',
            mt: 6,
            textAlign: 'center',
            fontSize: { xs: '2rem', sm: '2rem', md: '3rem' },
            fontWeight: 700,
            letterSpacing: '0.5px',
            fontFamily: 'Comfortaa'
          }}>
            {t('faq.title')}
          </Typography>
          <Typography variant="h6" sx={{ 
            color: '#68f1d1',
            textAlign: 'center',
            maxWidth: '800px',
            mt: 1,
            mb: 3,
            mx: 'auto',
            opacity: 0.8
          }}>
            {t('faq.subtitle')}
          </Typography>
        </Box>

        <Paper 
          elevation={5} 
          sx={{ 
            p: { xs: 2, sm: 4 }, 
            borderRadius: 1,
            background: 'linear-gradient(145deg, rgba(45, 46, 73, 0.7) 0%, rgba(35, 36, 63, 0.9) 100%)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(104, 241, 209, 0.1)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          }}
        >
          {/* Balisage Schema.org pour FAQ */}
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqItems.map(item => ({
              "@type": "Question",
              "name": item.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": item.answer
              }
            }))
          })}} />

          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" sx={{ color: 'white', opacity: 0.9, mb: 2 }}>
              {t('faq.introduction')}
            </Typography>
          </Box>

          <Divider sx={{ borderColor: 'rgba(104, 241, 209, 0.2)', mb: 4 }} />

          {faqItems.map((item, index) => (
            <Accordion 
              key={index}
              sx={{ 
                background: 'transparent',
                color: 'white',
                mb: 2,
                border: '1px solid rgba(104, 241, 209, 0.2)',
                borderRadius: '25px !important',
                '&:before': { display: 'none' },
                '&.Mui-expanded': { 
                  margin: '0 0 16px 0',
                  borderColor: 'rgba(104, 241, 209, 0.5)',
                }
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon sx={{ color: '#68f1d1' }} />}
                sx={{ 
                  borderRadius: '25px',
                  '&.Mui-expanded': { borderBottom: '1px solid rgba(104, 241, 209, 0.2)' }
                }}
              >
                <Typography sx={{ fontWeight: 600, color: '#68f1d1' }}>
                  {item.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ background: 'var(--fd-gridDreams)' }}>
                <Typography sx={{ color: 'white', opacity: 0.9, whiteSpace: 'pre-line' }}>
                  {item.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      </Container>
    </>
  );
};

export default FAQ;
